import {
  Shift,
  ShiftPayload,
  useCreateShiftMutation,
  useDeleteShiftMutation,
  useSaveShiftMutation,
  useUpdateShiftMutation,
} from "@ots-frontend/ots-data";
import {useCallback, useEffect} from "react";
import {notifications} from "@mantine/notifications";
import {ResultTypeFrom} from "@reduxjs/toolkit/query";
import {useTranslation} from "react-i18next";
import {useDebouncedState} from "@mantine/hooks";
import {modals} from "@mantine/modals";
import {Text} from "@mantine/core";

export type UseShiftMutationProps = {}

export type UseShiftMutationResult = {
  saveShift: (shift: ShiftPayload, onSuccess: (() => void) | undefined) => void
  createShift: (shift: ShiftPayload, onSuccess: (() => void) | undefined) => void
  updateShift: (shift: ShiftPayload, onSuccess: (() => void) | undefined) => void
  deleteShift: (id: string, onSuccess: (() => void) | undefined) => void

  isSaving?: boolean
  isCreating?: boolean
  isUpdating?: boolean
  isDeleting?: boolean
}

export const useShiftMutation = (props: UseShiftMutationProps = {}): UseShiftMutationResult => {
  const {t} = useTranslation(["common"])

  // state
  const [_saving, _setSaving] = useDebouncedState(false, 500)
  const [_creating, _setCreating] = useDebouncedState(false, 500)
  const [_updating, _setUpdating] = useDebouncedState(false, 500)
  const [_deleting, _setDeleting] = useDebouncedState(false, 500)

  // possible mutations
  const [_saveShift, {isLoading: isSaving}] = useSaveShiftMutation()
  const [_createShift, {isLoading: isCreating}] = useCreateShiftMutation()
  const [_updateShift, {isLoading: isUpdating}] = useUpdateShiftMutation()
  const [_deleteShift, {isLoading: isDeleting}] = useDeleteShiftMutation()

  useEffect(() => {
    _setSaving(isSaving)
  }, [isSaving, _setSaving])

  useEffect(() => {
    _setCreating(isCreating)
  }, [isCreating, _setCreating])

  useEffect(() => {
    _setUpdating(isUpdating)
  }, [isUpdating, _setUpdating])

  useEffect(() => {
    _setDeleting(isDeleting)
  }, [isDeleting, _setDeleting])

  useEffect(() => {
    if (_saving) {
      notifications.show({
        id: "useShiftUpdate_saving",
        title: t("common.status.saving", "Saving") + " " + t("common.entity.shift", "Shift"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useShiftUpdate_saving")
    }
  }, [_saving, t]);

  useEffect(() => {
    if (_creating) {
      notifications.show({
        id: "useShiftUpdate_creating",
        title: t("common.status.creating", "Creating") + " " + t("common.entity.shift", "Shift"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useShiftUpdate_creating")
    }
  }, [_creating, t]);

  useEffect(() => {
    if (_updating) {
      notifications.show({
        id: "useShiftUpdate_updating",
        title: t("common.status.updating", "Updating") + " " + t("common.entity.shift", "Shift"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useShiftUpdate_updating")
    }
  }, [_updating, t]);

  useEffect(() => {
    if (_deleting) {
      notifications.show({
        id: "useShiftUpdate_deleting",
        title: t("common.status.deleting", "Deleting") + " " + t("common.entity.shift", "Shift"),
        message: "",
        loading: true,
        autoClose: false,
      });
    } else {
      notifications.hide("useShiftUpdate_deleting")
    }
  }, [_deleting, t]);

  const handleSaveResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.shift", "Shift") + " " + t("common.status.saved", "Saved"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleCreateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.shift", "Shift") + " " + t("common.status.created", "Created"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleUpdateResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.shift", "Shift") + " " + t("common.status.updated", "Updated"),
          message: ""
        });
        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteResult = useCallback((promise: Promise<ResultTypeFrom<any>>, onSuccess: (() => void) | undefined) => {
    promise
      .then(() => {
        notifications.show({
          color: "green",
          title: t("common.entity.shift", "Shift") + " " + t("common.status.deleted", "Deleted"),
          message: ""
        });

        if (onSuccess) {
          onSuccess()
        }
      })
      .catch((rejected) =>
        notifications.show({
          color: "red",
          title: t("common.error", "Error"),
          message: rejected.data?.detail ?? t("common.error", "Error"),
        })
      );
  }, [t])

  const handleDeleteConfirm = useCallback((id: string, onSuccess: (() => void) | undefined = undefined) => {
    const promise = _deleteShift({id: id}).unwrap()
    handleDeleteResult(promise, onSuccess)
  }, [_deleteShift, handleDeleteResult])

  const saveShift = useCallback((shift: ShiftPayload, onSuccess: (() => void) | undefined) => {
    const promise = _saveShift(shift).unwrap()
    handleSaveResult(promise, onSuccess)
  }, [_saveShift, handleSaveResult])

  const createShift = useCallback((shift: ShiftPayload, onSuccess: (() => void) | undefined) => {
    const promise = _createShift(shift).unwrap()
    handleCreateResult(promise, onSuccess)
  }, [_createShift, handleCreateResult])

  const updateShift = useCallback((shift: ShiftPayload, onSuccess: (() => void) | undefined) => {
    const promise = _updateShift(shift).unwrap()
    handleUpdateResult(promise, onSuccess)
  }, [_updateShift, handleUpdateResult])

  const deleteShift = useCallback((id: string, onSuccess: (() => void) | undefined) => {
    modals.openConfirmModal({
      title: t("common.action.delete", "Delete") + " " + t("common.entity.shift", "Shift"),
      centered: true,
      children: (
        <Text size="sm" p="md">
          {t("common.action.deleteshift.confirm", "Are you sure you want to delete this shift?")}
        </Text>
      ),
      labels: {
        confirm: t("common.action.delete", "Delete"),
        cancel: t("common.action.cancel", "Cancel"),
      },
      confirmProps: {color: 'red'},
      onConfirm: () => {
        handleDeleteConfirm(id, onSuccess)
      },
    })
  }, [handleDeleteConfirm, t])

  return {
    saveShift: saveShift,
    createShift: createShift,
    updateShift: updateShift,
    deleteShift: deleteShift,
    isSaving: _saving,
    isCreating: _creating,
    isUpdating: _updating,
    isDeleting: _deleting,
  }
}
